// 채팅 패널(설계서 §9.6) — 세션 무대 옆에 붙는 대화 창, 또는 **별도 창**(§9.6.6)의 내용.
//
// 🔴 **무대(stage) 안에 둔다**(옆에 고정일 때). 마우스 리스너는 `<video>` 에만 걸려 있어 패널 위 클릭이 원격으로 새지 않는다
//    (파일 패널과 같은 이유·같은 자리).
// 🔴 **입력칸의 키·붙여넣기는 반드시 `stopPropagation()`** — 세션 화면의 키 입력은 전부 원격에 주입되고
//    Ctrl+V 도 가로채진다. 빠뜨리면 채팅에 타이핑한 글자가 그대로 상대 PC 에 입력된다.
//    (별도 창에서는 무대가 없어 새지 않지만, 같은 컴포넌트라 그대로 둔다 — 해가 없다.)
// 📌 2026-09-24: 종전 "팝업" 은 무대 안에서 끌어 옮기는 패널이었다 — 원격 화면 밖으로 못 나가 **별도 창**으로 바꿨다.
//    그래서 끌기 코드는 없다. 창의 위치·크기는 OS 가 맡는다.

import { useEffect, useRef, useState } from 'react';

import type { Chat } from '../chat/useChat';
import { remainingBytes } from '../chat/useChat';
import { MAX_TEXT_BYTES } from '../chat/protocol';
import { clampDockWidth, DOCK_W_DEFAULT, DOCK_W_MAX_RATIO } from '../chat/useChatLayout';
import { SaveNoticeBar } from './SaveNoticeBar';
import { stageIsolationProps } from './stageIsolation';

/** 패널이 놓인 자리 — `dock` = 세션 창 무대 옆 · `window` = 채팅 별도 창을 가득 채운다. */
export type ChatPanelPlace = 'dock' | 'window';

/**
 * 고정 패널 왼쪽 가장자리의 **폭 조절 손잡이**. 왼쪽으로 끌면 넓어지고, 두 번 누르면 기본 폭으로 돌아간다.
 *
 * - 🔴 **포인터 캡처**를 건다 — 끄는 동안 마우스가 영상(`<video>`) 위로 지나가도 이벤트가 손잡이로만 온다.
 *   캡처가 없으면 영상의 마우스 리스너가 움직임을 받아 **원격 PC 에 마우스 이동·클릭이 주입**된다.
 * - 폭 상한은 무대(패널의 부모) 폭의 [DOCK_W_MAX_RATIO] — 원격 화면이 너무 좁아지지 않게. 하한은 `DOCK_W_MIN`.
 * - 끄는 동안에는 저장하지 않고(움직임마다 localStorage 에 쓰지 않는다), 놓을 때 한 번 저장한다.
 */
function DockResizeHandle({ width, onChange }: { width: number; onChange: (w: number, persist: boolean) => void }) {
  const drag = useRef<{ startX: number; startW: number; maxPx: number; last: number } | null>(null);

  return (
    <div
      className="cpanel-resize"
      title="끌어서 채팅 폭 조절 · 두 번 누르면 기본 폭"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault(); // 끄는 동안 글자 선택이 생기지 않게
        e.stopPropagation();
        const stage = e.currentTarget.parentElement?.parentElement; // 손잡이 → 패널 → 무대
        const maxPx = (stage?.clientWidth ?? window.innerWidth) * DOCK_W_MAX_RATIO;
        drag.current = { startX: e.clientX, startW: width, maxPx, last: width };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        e.stopPropagation();
        // 패널은 오른쪽에 붙어 있다 → 왼쪽으로 끌수록(clientX 감소) 넓어진다.
        const w = clampDockWidth(d.startW + (d.startX - e.clientX), d.maxPx);
        if (w !== d.last) {
          d.last = w;
          onChange(w, false);
        }
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        if (!d) return;
        e.stopPropagation();
        drag.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
        onChange(d.last, true);
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onChange(DOCK_W_DEFAULT, true);
      }}
    />
  );
}

export function ChatPanel({
  chat,
  place,
  onToggleMode,
  onClose,
  banner,
  resize,
}: {
  chat: Chat;
  place: ChatPanelPlace;
  /**
   * 고정 패널의 폭 조절(2026-09-24 사용자 요구 "채팅창 크기 변경 가능하도록") — 왼쪽 가장자리를 끌어 바꾼다.
   * 별도 창은 OS 창이 크기를 맡으므로 넘기지 않는다.
   */
  resize?: { width: number; onChange: (w: number, persist: boolean) => void };
  /** 표시 방식 바꾸기(고정 ↔ 별도 창). */
  onToggleMode: () => void;
  /** 채팅 닫기(✕ · Esc). 고정이면 키보드 포커스를 세션 화면으로 되돌리는 것까지 호출부가 한다. */
  onClose: () => void;
  /** 패널 위쪽 안내(예: 별도 창을 못 띄워 고정으로 되돌림). 누르면 사라지게 하려면 호출부가 비운다. */
  banner?: { text: string; onClose: () => void };
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // 사용자가 위로 올려 옛 대화를 읽는 중이면 자동 스크롤을 멈춘다(읽는 중에 끌려 내려가면 거슬린다).
  const [pinned, setPinned] = useState(true);
  /** 크게 보는 이미지(패널 안에서 연다 — 새 창은 통합앱에서 열리지 않는다). */
  const [zoom, setZoom] = useState<string | null>(null);

  // 새 대화가 오면 맨 아래로(사용자가 위를 보고 있지 않을 때만).
  useEffect(() => {
    if (pinned && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [chat.entries, pinned]);

  // 패널을 열면 바로 쓸 수 있게 입력칸에 포커스 — 🔴 단 상대의 글이 와서 **저절로** 열렸으면 하지 않는다
  //    (담당자가 원격 화면에 치던 글자가 채팅 입력칸으로 샌다 — `Chat.openedQuietly`, 2026-09-29).
  useEffect(() => {
    if (!chat.openedQuietly) {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열린 순간 한 번만(열린 뒤 값이 바뀌어도 다시 잡지 않는다)
  }, []);

  const remaining = remainingBytes(chat.draft, MAX_TEXT_BYTES);

  function submit() {
    const text = chat.draft.trim();
    // 첨부만 보내는 것도 정상이다(그림 한 장만 건네는 경우).
    if (!text && !chat.pending) {
      return;
    }
    chat.send(text);
    inputRef.current?.focus();
  }

  return (
    // 🔴 `onMouseDown` 만으로는 부족하다 — 무대가 **`onClick`** 으로 IME 칸에 포커스를 되돌리는데,
    //    mousedown 의 stopPropagation 은 뒤따르는 click 의 버블링을 막지 않는다.
    //    파일 패널과 같은 차단 묶음을 쓴다(`stageIsolationProps`).
    <div className={`cpanel ${place}`} {...stageIsolationProps}>
      {resize && <DockResizeHandle width={resize.width} onChange={resize.onChange} />}
      <div className="cpanel-bar">
        <strong>채팅</strong>
        <span className="grow" />
        <button
          className="ghost"
          title={
            place === 'dock'
              ? '별도 창으로 띄우기 — 원격 화면을 넓게 쓰고, 대화 창은 바탕화면 원하는 곳에 둡니다'
              : '옆에 고정 — 원격 화면과 나란히 놓습니다(화면이 가려지지 않습니다)'
          }
          onClick={onToggleMode}
        >
          {place === 'dock' ? '⧉ 창으로' : '⇥ 고정'}
        </button>
        <button
          className="ghost"
          title="대화를 텍스트 파일로 저장"
          disabled={chat.entries.length === 0}
          onClick={chat.download}
        >
          💾 대화 저장
        </button>
        <button
          className="ghost"
          title="채팅 닫기"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {banner && (
        <div className="cpanel-blocked" onClick={banner.onClose} title="눌러서 닫기">
          {banner.text}
        </div>
      )}

      {/* 세션 한정 이력임을 처음부터 알린다 — 사람이 [대화 저장]을 누를 판단을 할 수 있어야 한다. */}
      <div className="cpanel-note">세션이 끝나면 대화는 사라집니다. 남기려면 [대화 저장]을 누르세요.</div>

      {chat.blockedReason && <div className="cpanel-blocked">{chat.blockedReason}</div>}

      <div
        className="cpanel-list"
        ref={listRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          // 바닥에서 40px 안이면 "따라가는 중" 으로 본다.
          setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
      >
        {chat.entries.length === 0 && <div className="cpanel-empty">아직 대화가 없습니다.</div>}
        {chat.entries.map((e) => (
          <div key={e.id} className={`cmsg ${e.from === 'me' ? 'me' : 'them'}`}>
            <div className="cmsg-body">
              {e.kind === 'image' && e.imageUrl ? (
                /* 🔴 종전에는 `<a target="_blank">` 로 감쌌는데 **통합앱에서는 새 창이 열리지 않아
                   눌러도 아무 일이 없었다**(2026-09-23 사용자 보고). `<a download>` 가 조용히 실패한 것과
                   같은 계열이다 ⇒ 앵커에 기대지 말고 **패널 안에서 크게 보기**로 바꿨다(어디서나 동작한다). */
                <img
                  src={e.imageUrl}
                  alt="주고받은 이미지"
                  title="눌러서 크게 보기"
                  onClick={() => setZoom(e.imageUrl ?? null)}
                />
              ) : (
                e.text
              )}
            </div>
            <div className="cmsg-meta">
              {new Date(e.at).toLocaleTimeString()}
              {/* 전달 표시는 **내가 보낸 것에만** — 상대 화면에 놓였다는 뜻이고, 읽었다는 뜻은 아니다. */}
              {e.from === 'me' && e.delivered === true && <span title="상대 화면에 표시됨"> · 전달됨</span>}
              {e.from === 'me' && e.error && <span className="cmsg-err"> · {e.error}</span>}
            </div>
          </div>
        ))}
      </div>

      {chat.error && (
        <div className="cpanel-err" onClick={chat.clearError} title="눌러서 닫기">
          {chat.error}
        </div>
      )}

      {/* 저장 안내 — 🔴 성공도 보인다(경로 · [폴더 열기]). 2026-09-24 Mac 실기: 저장은 됐는데 "반응 없음" 으로 보였다. */}
      {chat.notice && <SaveNoticeBar notice={chat.notice} onClose={chat.clearNotice} className="cpanel-saved" />}

      {/* 첨부 미리보기(§9.6.7) — 붙여넣은 그림은 **여기서 기다렸다가** [보내기]/Enter 에 나간다.
          붙이자마자 보내면 잘못 복사한 그림이 되돌릴 수 없이 상대 화면에 떠 버린다. */}
      {chat.pending && (
        <div className="cpanel-attach">
          <img src={chat.pending.url} alt="첨부한 이미지" onClick={() => setZoom(chat.pending?.url ?? null)} />
          <span className="cpanel-attach-note">보내면 상대 화면에 표시됩니다</span>
          <button className="ghost" title="첨부 취소" onClick={chat.clearAttachment}>
            ✕
          </button>
        </div>
      )}

      <div className="cpanel-input">
        <textarea
          ref={inputRef}
          rows={2}
          value={chat.draft}
          disabled={!chat.canSend}
          placeholder={
            chat.canSend
              ? chat.canSendImage
                ? '메시지 입력 — Enter 로 보내기, 이미지는 붙여넣기(Ctrl+V)'
                : '메시지 입력 — Enter 로 보내기'
              : '보낼 수 없습니다'
          }
          onChange={(e) => chat.setDraft(e.target.value)}
          // 🔴 키가 원격으로 새지 않게 — 이것이 없으면 타이핑이 상대 PC 에 입력된다.
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // 줄바꿈 대신 보내기(Shift+Enter 는 줄바꿈)
              submit();
            }
            if (e.key === 'Escape') {
              onClose();
            }
          }}
          onKeyUp={(e) => e.stopPropagation()}
          // 🔴 붙여넣기도 가로챈다 — 기본 경로는 원격 클립보드로 보내 버린다.
          onPaste={(e) => {
            e.stopPropagation();
            // 🔴 판정은 **동기**다. 종전에는 `await` 뒤에서 preventDefault 를 불러 **이미 늦었고**,
            //    그래서 macOS 스크린샷을 붙여넣으면 **파일 이름이 입력칸에 함께 들어갔다**.
            if (chat.attach(e.clipboardData)) {
              e.preventDefault();
            }
          }}
        />
        <button
          className="ghost"
          disabled={!chat.canSend || (chat.draft.trim().length === 0 && !chat.pending)}
          onClick={submit}
        >
          보내기
        </button>
      </div>
      {/* 남은 여유가 얼마 없을 때만 알린다(늘 띄우면 잔소리가 된다). */}
      {chat.canSend && remaining < 500 && (
        <div className="cpanel-note">{remaining >= 0 ? `${remaining}바이트 남음` : '글이 너무 깁니다'}</div>
      )}

      {/* 크게 보기 — 패널 **안**에 띄운다. 새 창(`target="_blank"`)은 통합앱에서 열리지 않는다. */}
      {zoom && (
        <div className="cpanel-zoom" onClick={() => setZoom(null)} title="눌러서 닫기">
          <img src={zoom} alt="크게 본 이미지" />
        </div>
      )}
    </div>
  );
}
